import { VideoContainerUi } from 'common/components/entities/Video/ui/VideoContainerUi'
import VideoUi from 'common/components/entities/Video/ui/VideoUi'
import { BorderType } from 'common/types/styleTypes'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import ReactPlayer from 'react-player/lazy'
import UnMute from './ui/UnMuteUi'

type BasePlayerProps = {
  attrId: string
  url: string
  autoplay: boolean
  playing?: boolean
  setPlaying?: React.Dispatch<React.SetStateAction<boolean>>
  controls?: boolean
  border?: Partial<BorderType>
  mobileBorder?: Partial<BorderType>
  boxShadow?: string
  mobileBoxShadow?: string
  onReady?: (video: ReactPlayer) => void
  posterUrl?: string
}

function BasePlayer({
  attrId,
  url,
  border,
  mobileBorder,
  controls = false,
  autoplay = false,
  playing = false,
  setPlaying,
  boxShadow,
  mobileBoxShadow,
  onReady,
  posterUrl,
}: BasePlayerProps) {
  const [muted, setMuted] = useState(false)
  const videoRef = useRef<ReactPlayer>(null)
  const [shouldBeLoaded, setShouldBeLoaded] = useState(false)

  const observerRef = useRef<IntersectionObserver | null>(null)

  // only render react-player if it was in the viewport at some point
  const containerRefCallback = useCallback((node: HTMLDivElement | null) => {
    if (!node || shouldBeLoaded) {
      return
    }

    observerRef.current?.disconnect()

    observerRef.current = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setShouldBeLoaded(true)
        observerRef.current?.disconnect()
        observerRef.current = null
      }
    })

    observerRef.current.observe(node)
  }, [])

  async function playerPlay(volume: number) {
    try {
      if (videoRef.current) {
        const player = videoRef.current.getInternalPlayer()
        player.volume = volume
        if (player.play) {
          await player.play()
        } else if (player.playVideo) {
          await player.playVideo()
        } else {
          // disabled due to high amount of uninformative reports
          // rollbar.error(
          //   "Couldn't play video because the play method was not found.",
          //   { url },
          // )
        }
      }
    } catch {
      // disabled due to high amount of uninformative reports
      // rollbar.error("Couldn't play the video.", { url })
    }
  }

  async function playerPlayMuted() {
    setMuted(true)
    await playerPlay(0)
  }

  async function playerPlayWithSound() {
    setMuted(false)
    await playerPlay(1)
  }

  const onError = async (e: any) => {
    if (e.name === 'NotAllowedError') {
      await playerPlayMuted()
    }
  }

  // fixes autoplay issue on lecture pages
  useEffect(() => {
    if (autoplay) {
      playerPlayMuted()
    }
  }, [autoplay])

  if (!url) {
    return (
      <VideoContainerUi
        border={border}
        mobileBorder={mobileBorder}
        boxShadow={boxShadow}
        mobileBoxShadow={mobileBoxShadow}
      >
        <VideoUi id={attrId} disabledPointerEvents={false} />
      </VideoContainerUi>
    )
  }

  return (
    <VideoContainerUi
      ref={containerRefCallback}
      border={border}
      mobileBorder={mobileBorder}
      boxShadow={boxShadow}
      mobileBoxShadow={mobileBoxShadow}
    >
      <VideoUi id={attrId} disabledPointerEvents={false}>
        {muted && <UnMute onClick={playerPlayWithSound} />}
        {shouldBeLoaded && (
          <ReactPlayer
            ref={videoRef}
            width="100%"
            height="100%"
            url={url}
            className="react-player"
            playing={playing}
            onError={onError}
            onReady={onReady}
            onPlay={() => setPlaying?.(true)}
            onStart={() => {
              const player = videoRef.current?.getInternalPlayer()
              if (player) {
                player.volume = 1 // need for YouTube, Vimeo plays with sound
              }
            }}
            muted={muted}
            controls={controls}
            playsinline={true}
            config={{
              file: {
                attributes: {
                  controlsList: 'nodownload',
                  poster: posterUrl,
                },
              },
              youtube: {
                // Called when state changes to unstarted (usually when video fails to autoplay)
                onUnstarted: playerPlayMuted,
              },
            }}
          />
        )}
      </VideoUi>
    </VideoContainerUi>
  )
}

export default BasePlayer
